<script setup lang="ts">
import {
  ElButton,
  ElCard,
  ElCollapse,
  ElCollapseItem,
  ElIcon,
  ElSwitch,
  ElTag,
  ElTooltip,
} from 'element-plus'
import { Location, MapLocation, ZoomIn } from '@element-plus/icons-vue'
import mapboxgl from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import { computed, onMounted, onUnmounted, ref } from 'vue'
import {
  GeoFencesApi,
  type SavedGeoFenceWithLocation,
} from '~/services/apiClientGps'
import { parseWKTPolygon } from '~/utils/wkt'

// Initialize Mapbox with your access token
mapboxgl.accessToken =
  'pk.eyJ1Ijoib21pZHJvc2hhbmllIiwiYSI6ImNtOGxpaXkwNjBrMWkyaXF1dWtvYmh2b2kifQ.1V8b-WsQ6qkqveYjXgCvdg'

const api = new GeoFencesApi()
const geoFences = ref<SavedGeoFenceWithLocation[]>([])
const selectedLocation = ref<string>('')
const selectedGeoFence = ref<SavedGeoFenceWithLocation | null>(null)
const mapContainer = ref<HTMLElement | null>(null)
const map = ref<mapboxgl.Map | null>(null)
const isSatelliteView = ref(false)

// Map style URLs
const MAP_STYLES = {
  streets: 'mapbox://styles/mapbox/streets-v12',
  satellite: 'mapbox://styles/mapbox/satellite-streets-v12',
}

// Group geofences by location
const locationGroups = computed(() => {
  const groups = new Map<string, SavedGeoFenceWithLocation[]>()

  geoFences.value.forEach((fence) => {
    const locationName = fence.location.name
    if (!groups.has(locationName)) {
      groups.set(locationName, [])
    }
    groups.get(locationName)?.push(fence)
  })

  // Convert to array and sort by location name
  return Array.from(groups.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([name, fences]) => ({
      name,
      fences,
      count: fences.length,
    }))
})

// Function to show multiple geofences on the map
const showGeoFencesOnMap = (fences: SavedGeoFenceWithLocation[]) => {
  if (!map.value) return

  // Clear existing layers and sources
  fences.forEach((_, index) => {
    const layerId = `geofence-${index}`
    if (map.value?.getLayer(layerId)) {
      map.value.removeLayer(layerId)
      map.value.removeSource(layerId)
    }
  })

  // Calculate bounds for all fences
  const bounds = new mapboxgl.LngLatBounds()

  fences.forEach((fence, index) => {
    const coordinates = parseWKTPolygon(fence.polygon)
    if (!coordinates.length) {
      console.error('No valid coordinates found for geofence:', fence)
      return
    }

    // Add source and layer for each fence
    const layerId = `geofence-${index}`
    map.value?.addSource(layerId, {
      type: 'geojson',
      data: {
        type: 'Feature',
        geometry: {
          type: 'Polygon',
          coordinates,
        },
        properties: {
          name: fence.name,
          id: fence.id,
        },
      },
    })

    map.value?.addLayer({
      id: layerId,
      type: 'fill',
      source: layerId,
      paint: {
        'fill-color': '#088',
        'fill-opacity': 0.4,
        'fill-outline-color': '#ff3b30',
      },
    })

    // Extend bounds
    coordinates[0].forEach((coord) => {
      bounds.extend(coord)
    })
  })

  // Fit map to show all fences
  if (!bounds.isEmpty()) {
    map.value?.fitBounds(bounds, {
      padding: 50,
      duration: 1000,
    })
  }
}

// Function to show a single geofence on the map
const showSingleGeoFenceOnMap = (fence: SavedGeoFenceWithLocation) => {
  if (!map.value) return

  // Clear existing layers and sources
  const existingLayers = map.value.getStyle().layers || []
  existingLayers.forEach((layer) => {
    if (layer.id.startsWith('geofence-')) {
      map.value?.removeLayer(layer.id)
      map.value?.removeSource(layer.id)
    }
  })

  const coordinates = parseWKTPolygon(fence.polygon)
  if (!coordinates.length) {
    console.error('No valid coordinates found for geofence:', fence)
    return
  }

  // Add source and layer for the fence
  const layerId = `geofence-${fence.id}`
  map.value.addSource(layerId, {
    type: 'geojson',
    data: {
      type: 'Feature',
      geometry: {
        type: 'Polygon',
        coordinates,
      },
      properties: {
        name: fence.name,
        id: fence.id,
      },
    },
  })

  map.value.addLayer({
    id: layerId,
    type: 'fill',
    source: layerId,
    paint: {
      'fill-color': '#088',
      'fill-opacity': 0.4,
      'fill-outline-color': '#ff3b30',
    },
  })

  // Fit map to the geofence bounds
  const bounds = new mapboxgl.LngLatBounds()
  coordinates[0].forEach((coord) => {
    bounds.extend(coord)
  })

  map.value.fitBounds(bounds, {
    padding: 50,
    duration: 1000,
  })
}

// Function to toggle map style
const toggleMapStyle = (value: string | number | boolean) => {
  if (!map.value) return

  const style = value ? MAP_STYLES.satellite : MAP_STYLES.streets

  map.value.setStyle(style)

  // Wait for the style to load before reapplying geofences
  map.value.once('style.load', () => {
    if (selectedGeoFence.value) {
      // If there's a selected geofence, show only that one
      showSingleGeoFenceOnMap(selectedGeoFence.value)
    } else if (selectedLocation.value) {
      // Otherwise, if there's a selected location, show all its fences
      const locationFences = locationGroups.value.find(
        (g) => g.name === selectedLocation.value
      )
      if (locationFences) {
        showGeoFencesOnMap(locationFences.fences)
      }
    }
  })
}

// Handle individual geofence click
const handleGeoFenceClick = (
  event: Event,
  fence: SavedGeoFenceWithLocation
) => {
  event.stopPropagation() // Prevent location collapse toggle
  selectedGeoFence.value = fence
  showSingleGeoFenceOnMap(fence)
}

onMounted(async () => {
  // Initialize map
  if (mapContainer.value) {
    map.value = new mapboxgl.Map({
      container: mapContainer.value,
      style: MAP_STYLES.streets,
      center: [-118.2437, 33.7271], // Center on Long Beach port area
      zoom: 12,
    })
  }

  // Fetch geofences
  try {
    const response = await api.getAllGeoFencesGeoFencesGet()
    geoFences.value = response.data
  } catch (error) {
    console.error('Error fetching geofences:', error)
  }
})

onUnmounted(() => {
  if (map.value) {
    map.value.remove()
  }
})
</script>

<template>
  <div class="gps-container">
    <ElCard class="gps-card">
      <template #header>
        <div class="card-header">
          <div class="header-left">
            <ElIcon class="header-icon"><MapLocation /></ElIcon>
            <h2>Geo Fences by Location</h2>
          </div>
          <div class="map-controls">
            <ElTooltip
              content="Toggle between street and satellite view"
              placement="top"
            >
              <div class="control-group">
                <span class="switch-label">Satellite View</span>
                <ElSwitch
                  v-model="isSatelliteView"
                  class="map-style-switch"
                  @change="toggleMapStyle"
                />
              </div>
            </ElTooltip>
          </div>
        </div>
      </template>
      <div class="content-container">
        <div class="locations-sidebar">
          <div class="sidebar-header">
            <h3>Locations</h3>
            <ElTag type="success" class="total-count">
              {{ geoFences.length }} Total Fences
            </ElTag>
          </div>
          <ElCollapse v-model="selectedLocation" class="custom-collapse">
            <ElCollapseItem
              v-for="group in locationGroups"
              :key="group.name"
              :title="group.name"
              :name="group.name"
            >
              <template #title>
                <div class="location-header">
                  <ElIcon class="location-icon"><Location /></ElIcon>
                  <span class="location-name">{{ group.name }}</span>
                  <ElTag
                    size="small"
                    type="info"
                    class="count-tag"
                    effect="dark"
                  >
                    {{ group.count }}
                  </ElTag>
                </div>
              </template>
              <div class="fence-list">
                <div
                  v-for="fence in group.fences"
                  :key="fence.id"
                  class="fence-item"
                  :class="{ selected: selectedGeoFence?.id === fence.id }"
                  @click="(event) => handleGeoFenceClick(event, fence)"
                >
                  <div class="fence-content">
                    <div class="fence-name">{{ fence.name }}</div>
                    <ElButton
                      type="primary"
                      size="small"
                      class="zoom-button"
                      @click.stop="showSingleGeoFenceOnMap(fence)"
                    >
                      <ElIcon><ZoomIn /></ElIcon>
                    </ElButton>
                  </div>
                  <div class="fence-meta">
                    ID: {{ fence.id }} • Location: {{ fence.location.name }}
                  </div>
                </div>
              </div>
            </ElCollapseItem>
          </ElCollapse>
        </div>
        <div class="map-section">
          <div ref="mapContainer" class="map-container"></div>
          <div class="map-overlay">
            <div v-if="selectedGeoFence" class="selected-info">
              <h4>Selected Fence</h4>
              <p>{{ selectedGeoFence.name }}</p>
            </div>
          </div>
        </div>
      </div>
    </ElCard>
  </div>
</template>

<style scoped>
.gps-container {
  padding: 20px;
  height: calc(100vh - 100px);
  background-color: #f5f7fa;
}

.gps-card {
  height: 100%;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.1);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-icon {
  font-size: 24px;
  color: var(--el-color-primary);
}

.header-left h2 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #2c3e50;
}

.map-controls {
  display: flex;
  align-items: center;
  gap: 16px;
}

.control-group {
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: #f8f9fa;
  padding: 6px 12px;
  border-radius: 8px;
}

.switch-label {
  font-size: 14px;
  color: #606266;
  font-weight: 500;
}

.content-container {
  display: flex;
  gap: 24px;
  height: calc(100vh - 300px);
  padding-top: 8px;
}

.locations-sidebar {
  flex: 1;
  min-width: 360px;
  max-width: 400px;
  display: flex;
  flex-direction: column;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
  height: 100%;
  overflow: hidden;
}

.sidebar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #ebeef5;
  flex-shrink: 0;
}

.sidebar-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #2c3e50;
}

.total-count {
  font-weight: 500;
}

.custom-collapse {
  flex: 1;
  overflow-y: auto;
  height: 0;
}

.location-header {
  display: flex;
  align-items: center;
  gap: 12px;
}

.location-icon {
  font-size: 18px;
  color: var(--el-color-primary);
}

.location-name {
  font-weight: 500;
  color: #2c3e50;
}

.count-tag {
  margin-left: auto;
}

.fence-list {
  padding: 8px 0;
  max-height: 300px;
  overflow-y: auto;
}

.fence-item {
  padding: 12px 16px;
  border-bottom: 1px solid #ebeef5;
  cursor: pointer;
  transition: all 0.2s ease;
}

.fence-item:hover {
  background-color: #f5f7fa;
  transform: translateX(4px);
}

.fence-item.selected {
  background-color: var(--el-color-primary-light-9);
  border-left: 3px solid var(--el-color-primary);
}

.fence-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.fence-name {
  font-weight: 500;
  color: #2c3e50;
}

.zoom-button {
  opacity: 0;
  transition: opacity 0.2s ease;
}

.fence-item:hover .zoom-button {
  opacity: 1;
}

.fence-meta {
  margin-top: 4px;
  font-size: 12px;
  color: #909399;
}

.map-section {
  flex: 2;
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
}

.map-container {
  height: 100%;
  border-radius: 8px;
}

.map-overlay {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 1;
}

.selected-info {
  background-color: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(8px);
  padding: 12px 16px;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

.selected-info h4 {
  margin: 0 0 4px;
  font-size: 14px;
  color: #606266;
}

.selected-info p {
  margin: 0;
  font-size: 16px;
  font-weight: 500;
  color: #2c3e50;
}

:deep(.el-collapse-item__header) {
  font-size: 16px;
  font-weight: 500;
  padding: 12px 16px;
}

:deep(.el-collapse-item__content) {
  padding-bottom: 0;
}

:deep(.el-collapse-item__wrap) {
  border-bottom: none;
}

/* Add custom scrollbar styling */
.custom-collapse::-webkit-scrollbar,
.fence-list::-webkit-scrollbar {
  width: 6px;
}

.custom-collapse::-webkit-scrollbar-track,
.fence-list::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.custom-collapse::-webkit-scrollbar-thumb,
.fence-list::-webkit-scrollbar-thumb {
  background: #dcdfe6;
  border-radius: 3px;
}

.custom-collapse::-webkit-scrollbar-thumb:hover,
.fence-list::-webkit-scrollbar-thumb:hover {
  background: #c0c4cc;
}
</style>
